import { useReducer, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router';
import { STORAGE_KEY } from '../../shared/config';
import { useTitle } from '../../shared/hooks/useTitle';
import { setItem } from '../../shared/services/storage/storage';
import NavigationButtons from '../components/NavigationButtons';
import PageContainer from '../components/PageContainer';
import Progress from '../components/Progress';
import { fields, formTitles, formSubtitles, descriptions, stepsLength } from '../constants/fields';
import EnrollForm from '../forms/EnrollForm';
import { getErrorsFromStep } from '../utils/getErrorsFromStep';
import { reducer, initState } from '../state/state';
import type { Fields, State } from '../types';
import PageHeader from '../components/PageHeader';
import NotValidLink from '../components/NotValidLink';
import { isCarrierFieldEnabled } from '../utils/isFieldEnabled';
import { getUrl } from '../utils/getUrl';
import { getLabel } from '../utils/getLabel';
import { scrollToConf } from '../constants/scrollToConf';
import GeneralError from '../components/GeneralError';

type EnrollProps = {
  step: number;
};

const Enroll = ({ step }: EnrollProps) => {
  const actualStep = Number(step);
  const title = formTitles[actualStep] || 'Missing title';
  const subtitle = formSubtitles[actualStep] || 'Missing subtitle';
  const description = descriptions[actualStep] || '';

  useTitle(title);

  const emptyState: State = {};
  const [state, dispatch] = useReducer(reducer, emptyState, initState(fields as Fields[]));
  const [isCarrierEnabled, setIsCarrierEnabled] = useState(() => isCarrierFieldEnabled(state));

  useEffect(() => {
    setItem(STORAGE_KEY.B2C_STATE, state);
    setIsCarrierEnabled(isCarrierFieldEnabled(state));
  }, [state]);

  useHotkeys('enter', () => handleChangeStep(step, 'next'));
  useHotkeys('shift+enter', () => handleChangeStep(step, 'prev'));

  const navigate = useNavigate();

  const handleChangeStep = async (step: number, direction: string) => {
    const stepErrors = getErrorsFromStep(state, step, dispatch);

    if (stepErrors.length > 0) {
      dispatch({ type: `set_step_has_errors`, value: { fieldName: '', fieldValue: step } });
      return false;
    }

    window.scrollTo(scrollToConf);
    navigate(getUrl({ direction, step, totalSteps: stepsLength, isCarrierEnabled }));
  };

  if (step > fields.length) {
    return <NotValidLink />;
  }

  const nextButtonLabel = getLabel({ step, totalSteps: stepsLength, isCarrierEnabled });

  if (actualStep === 7 && state.Data.value === '') {
    dispatch({
      type: 'set_Data',
      value: { fieldName: 'Data' },
    });
  }

  return (
    <PageContainer>
      <>
        <PageHeader title={title} subtitle={subtitle} description={description} />
        <EnrollForm step={actualStep} state={state} dispatch={dispatch} handleChangeStep={handleChangeStep} />
        <>
          {state.hasErrors?.fieldValue === step && (
            <GeneralError message={'Ci sono errori nei campi, non è possibile procedere'} />
          )}
          <NavigationButtons
            prev={{
              condition: step > 0,
              label: `Indietro (step ${step})`,
              action: () => handleChangeStep(step, 'prev'),
              color: 'primary',
            }}
            next={{
              label: nextButtonLabel,
              action: () => handleChangeStep(step, 'next'),
              color: state.hasErrors === step ? 'error' : 'primary',
            }}
          />
          <Progress step={step} totalSteps={stepsLength + 1} titles={formTitles} />
        </>
      </>
    </PageContainer>
  );
};

export default Enroll;
